import type { QueryClient } from '@tanstack/react-query';
import type { FieldMetadata, RecordRow } from './client';
import { recordStillMatches } from './filterMatch';
import { queryKeys, type RecordListSignature } from './queryKeys';

/**
 * After a write: does the record still belong in the lists that are FILTERED?
 *
 * This is the call site INV-6's JS evaluator exists for. A table filtered to "Stage is
 * Active" that just had a row's stage set to Churned must drop that row now, not when a
 * refetch happens to land - otherwise the user is looking at a row the server would no
 * longer return, and the next edit they make is on a record they think is in this view.
 *
 * The rules, and why:
 *  - NO LONGER MATCHES: the id is removed from the list entry. Cheap, exact, and it keeps
 *    the canonical record entry (INV-13) untouched - the record still exists, it is just
 *    not in this view.
 *  - NOW MATCHES but is absent: the entry is invalidated instead of guessed at, because
 *    WHERE the row belongs depends on the sort and the cursor, and inventing a position is
 *    how a list starts disagreeing with the next page.
 *  - filter cannot be evaluated (unknown field, operand the browser refuses): the record
 *    is kept (see `recordStillMatches`) and the server settles it.
 *
 * The field metadata comes from the cache rather than from a parameter, so no call site has
 * to thread it through: `['fields', objectId]` is already loaded by every screen that can
 * render a filter.
 */
export function reconcileFilteredLists(
  queryClient: QueryClient,
  objectId: string,
  record: RecordRow,
): void {
  /**
   * THE GROUPED READ, AND THIS LINE GOES FIRST (group-by-aggregation.md D25).
   *
   * `useUpdateRecord` is the one write path that does NOT invalidate the record read
   * prefixes - it calls this function instead, deliberately, because it patches the list
   * rather than refetching it. An edit that moves a record between groups is therefore the
   * single most likely write to invalidate a grouped read, and under a separate prefix it
   * reaches neither the prefix nor - see the walk below - the patcher. So the reconciler
   * invalidates the grouped entry itself: its own contract, "an edited record may have
   * left (or joined) a FILTERED list", applied to the one entry shape it cannot patch.
   *
   * BEFORE the cold-fields guard, and the placement is the decision. This function returns
   * early when `['fields', objectId]` is not in the cache, because it cannot evaluate a
   * filter without the field metadata. A grouped invalidation needs no fields at all -
   * placed after that guard it would be silently skipped on exactly the loads where the
   * fields cache is cold, which is the silent-miss class the separate prefix's whole
   * mechanism exists to remove, reintroduced by an implementation detail.
   *
   * The literal key is written here rather than taken from `invalidateRecordReads`,
   * because that helper also invalidates `['recordList', objectId]` - which is the one
   * thing this function must never do. This file and `live/invalidation.ts` are the two
   * obligation 41 exempts, and this is why.
   */
  void queryClient.invalidateQueries({ queryKey: ['recordGroups', objectId] });

  const fields = queryClient.getQueryData<readonly FieldMetadata[]>(queryKeys.fields(objectId));
  if (fields === undefined) return;

  const entries = queryClient.getQueryCache().findAll({ queryKey: ['recordList', objectId] });
  for (const entry of entries) {
    const signature = signatureOf(entry.queryKey);
    const filter = signature?.filter;
    if (filter === undefined) continue;

    const data = entry.state.data as ListEntryData | undefined;
    if (data === undefined) continue;

    const pages = 'pages' in data ? data.pages : [data];
    const listed = pages.some((page) => page.ids.includes(record.id));
    const matches = recordStillMatches(filter, fields, record);
    if (listed === matches) continue;

    /**
     * A LIST THAT PUBLISHES A COUNT CANNOT BE REPAIRED BY EDITING ITS ID ARRAY.
     *
     * `withTotalCount` is the SERVER's answer over the whole filtered set (D20), and
     * splicing one id out of the loaded page leaves that number saying what it said
     * before. On a board (D21) that is the visible failure: drag a card out of "Active"
     * and the header keeps claiming seven. So an entry carrying a count is INVALIDATED
     * in both directions rather than patched.
     *
     * That is one refetch per column per drop, and it is the trade D21 names and
     * accepts: a drop is a deliberate, low-frequency action, and the alternative is an
     * insertion path that would have to guess a row's position under the list's sort -
     * which is how a list starts disagreeing with its own next page.
     *
     * AND IT MUST NOT CANCEL A FETCH IN FLIGHT (`table-completion.md` D25). This is the
     * other invalidation that can land on a PAGED entry mid-append, and on
     * `invalidateQueries`' default it would kill the user's `Show more` silently. The
     * literal `false` is right here where `cancelRefetchFor` is right everywhere else: this
     * loop only ever holds `['recordList', ...]` keys, so the predicate would have exactly
     * one answer. The grouped invalidation above keeps the default, for the reason it is
     * written separately at all.
     */
    if (matches || publishesCount(data)) {
      void queryClient.invalidateQueries({ queryKey: entry.queryKey }, { cancelRefetch: false });
      continue;
    }
    queryClient.setQueryData(
      entry.queryKey,
      'pages' in data
        ? { ...data, pages: data.pages.map((page) => withoutId(page, record.id)) }
        : withoutId(data, record.id),
    );
  }
}

interface ListPage {
  readonly ids: readonly string[];
  readonly totalCount?: number;
}

function publishesCount(data: ListEntryData): boolean {
  const first = 'pages' in data ? data.pages[0] : data;
  return first?.totalCount !== undefined;
}

/** The two shapes `['recordList', objectId, ...]` can hold: one page, or every loaded page. */
type ListEntryData = ListPage | { readonly pages: readonly ListPage[] };

function withoutId<T extends ListPage>(page: T, recordId: string): T {
  return { ...page, ids: page.ids.filter((id) => id !== recordId) };
}

/**
 * The signature, from EITHER list key.
 *
 * `useRecordList` keys `[recordList, objectId, signature]`; `useInfiniteRecordList` keys
 * `[recordList, objectId, 'pages', signature]` because the two hooks store different
 * shapes under the same prefix. Reading slot 2 blindly - which is what this did - meant
 * the paged form silently fell through the `filter === undefined` guard and never
 * reconciled: a row edited out of a filtered related section would sit there until a
 * refetch, which is open risk 4 happening in the quietest possible way.
 */
function signatureOf(queryKey: readonly unknown[]): RecordListSignature | undefined {
  const slot = queryKey[2] === 'pages' ? queryKey[3] : queryKey[2];
  return typeof slot === 'object' && slot !== null ? (slot as RecordListSignature) : undefined;
}
